import { Static, Type } from '@sinclair/typebox';

import { ACTIVE_WEBSITE_DOMAIN_STATUSES, WebsiteDomainActivationStatus } from './professional';

// =============================================================================
// Zone DNS self-service (plan MAX, scope `website.dns`)
// =============================================================================
// Le pro peut lister/créer/éditer/supprimer les enregistrements de la zone OVH
// de ses domaines. Les enregistrements gérés par la plateforme (routage site,
// mail Stalwart, DKIM, vérification GSC) sont verrouillés — cf. le classifier
// backend `dns-record-classifier.ts` qui est la seule source de vérité de ce
// verrouillage ; les champs `managed`/`editable`/`managed_reason` ci-dessous en
// sont la projection pour l'UI.
// =============================================================================

/**
 * Types d'enregistrements créables/éditables par le pro.
 * NS / SOA / DNAME exclus : la délégation de zone casserait le routage du site
 * et l'email — ces types restent visibles en lecture seule (UNSUPPORTED_TYPE).
 */
export const DNS_EDITABLE_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'SRV', 'CAA'] as const;
export type DnsEditableRecordType = (typeof DNS_EDITABLE_RECORD_TYPES)[number];

/** Raison pour laquelle un enregistrement est géré/verrouillé par la plateforme. */
export enum DnsManagedReason {
  /** A/AAAA apex et CNAME www : routage du site vers notre cluster. */
  SITE_ROUTING = 'SITE_ROUTING',
  /** MX/SPF/_dmarc quand l'hébergement mail Stalwart est actif sur le domaine. */
  MAIL_MANAGED = 'MAIL_MANAGED',
  /**
   * TXT `*._domainkey` : clés DKIM générées et tournées par Stalwart.
   * Verrouillé uniquement quand l'hébergement mail est actif — sans nos boîtes,
   * le pro doit pouvoir poser le DKIM de son provider externe (`google._domainkey`).
   */
  DKIM = 'DKIM',
  /** TXT apex `google-site-verification=` : preuve de propriété Search Console, à conserver. */
  GSC_VERIFICATION = 'GSC_VERIFICATION',
  /**
   * Expéditeur de campagnes e-mail sur ce domaine (Mailjet) : TXT `mailjet._domainkey`, TXT de
   * propriété `mailjet._<jeton>` et SPF apex (l'include y est fusionné). Verrouillés tant que le
   * domaine expéditeur est configuré (PENDING / DNS_PENDING / ACTIVE) ; le DKIM d'un autre provider
   * (`google._domainkey`) reste éditable.
   */
  CAMPAIGN_SENDER = 'CAMPAIGN_SENDER',
  /** Type hors allowlist (NS, SOA, pseudo-type SPF legacy…) : affiché mais non éditable. */
  UNSUPPORTED_TYPE = 'UNSUPPORTED_TYPE',
}

export const MAX_DNS_RECORDS_PER_ZONE = 100;
/** TTL minimum accepté par OVH. */
export const DNS_MIN_TTL = 60;
/** 7 jours. */
export const DNS_MAX_TTL = 604800;
export const DNS_DEFAULT_TTL = 3600;

/**
 * Sous-domaine d'un enregistrement : '' = apex, sinon labels séparés par des
 * points. Underscore autorisé (`_dmarc`, `_sip._tcp`), wildcard `*` uniquement
 * en label de tête. 63 chars max par label (RFC 1035).
 */
export const DNS_SUBDOMAIN_PATTERN =
  '^$|^(\\*\\.)?(_?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\\.)*_?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$|^\\*$';

const DNS_SUBDOMAIN_REGEX = new RegExp(DNS_SUBDOMAIN_PATTERN);

export function isValidDnsSubdomain(subdomain: string): boolean {
  return subdomain.length <= 253 && DNS_SUBDOMAIN_REGEX.test(subdomain);
}

const IPV4_REGEX = /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}$/;
// IPv6 : forme complète ou compressée `::`. Volontairement permissif sur les
// formes mixtes rares (IPv4-mapped) — OVH revalide de son côté.
const IPV6_REGEX =
  /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:)*::([0-9a-fA-F]{1,4}:)*[0-9a-fA-F]{0,4})$/;
const HOSTNAME_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9_]([a-z0-9-_]{0,61}[a-z0-9_])?)*\.?$/i;
// CAA : `flags tag "value"` (ex. `0 issue "letsencrypt.org"`).
const CAA_REGEX = /^(0|1|12[0-8]|1[01][0-9]|[1-9][0-9]?) (issue|issuewild|iodef) "[^"]*"$/;
// SRV (hors priorité, gérée à part) : `weight port target` (ex. `5 5060 sip.example.com.`).
const SRV_REGEX = /^\d{1,5} \d{1,5} [a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.?$/i;

export const MAX_DNS_TARGET_LENGTH = 1024;

/**
 * Normalise un `target` DNS : retire les guillemets d'encadrement (une paire)
 * et les espaces superflus. Partagé backend (comparaison de doublons, prépa
 * TXT avant validation) et frontend (même prépa dans le formulaire) — un TXT
 * collé avec ses guillemets depuis une doc vendor est ainsi accepté partout.
 */
export function normalizeDnsTarget(target: string): string {
  return target.replace(/^"|"$/g, '').trim();
}

/**
 * Valeur d'un TXT découpé en chunks (`"…" "…"`, ce qu'OVH renvoie au-delà de 255 caractères),
 * recollée comme le fait un résolveur DNS : bout à bout, SANS séparateur (RFC 7208 §3.3). OVH
 * peut couper au milieu d'un mécanisme : recoller avec un espace le casserait en deux termes.
 */
export function joinDnsTxtChunks(target: string): string {
  return normalizeDnsTarget(target.replace(/"\s+"/g, ''));
}

/** Nom d'hôte DNS comparable : minuscules, sans point final (`MX.Example.COM.` → `mx.example.com`). */
export function normalizeDnsHost(host: string): string {
  return host.trim().replace(/\.$/, '').toLowerCase();
}

/**
 * Pseudo-type hérité exposé par l'API OVH pour la politique SPF de l'apex (RFC 4408,
 * obsolète depuis la RFC 7208). Publié en TXT dans la zone, mais typé `SPF` côté API :
 * un tel record posé par une ancienne offre mail OVH (Email Pro, Zimbra) doit être
 * remplacé par un TXT `v=spf1`, jamais laissé à côté (deux politiques = permerror).
 */
export const OVH_LEGACY_SPF_FIELD_TYPE = 'SPF';

/** La valeur est-elle une politique SPF (`v=spf1 …`), guillemets d'encadrement tolérés ? */
export function isSpfValue(value: string): boolean {
  return normalizeDnsTarget(value ?? '')
    .toLowerCase()
    .startsWith('v=spf1');
}

/**
 * L'enregistrement est-il LA politique SPF de l'apex (TXT `v=spf1` ou pseudo-type `SPF` legacy) ?
 * Seule définition partagée backend (refus d'un second SPF, diagnostic messagerie, upsert mail) et
 * frontend (garde inline du formulaire, avertissement de suppression).
 */
export function isApexSpfRecord(record: { type: string; subdomain: string; target: string }): boolean {
  return (
    (record.subdomain ?? '') === '' &&
    (record.type === 'TXT' || record.type === OVH_LEGACY_SPF_FIELD_TYPE) &&
    isSpfValue(record.target)
  );
}

/**
 * Validation de la valeur d'un enregistrement selon son type, partagée
 * backend (rejet 400) et frontend (validation inline du formulaire).
 * La valeur attendue est « nue » : sans quotes TXT, sans priorité MX/SRV
 * (champ séparé — le backend compose le target OVH). Pour un TXT, passer la
 * valeur par `normalizeDnsTarget` AVANT validation.
 */
export function validateDnsRecordTarget(type: DnsEditableRecordType, target: string): boolean {
  const value = (target ?? '').trim();
  if (!value || value.length > MAX_DNS_TARGET_LENGTH) return false;
  switch (type) {
    case 'A':
      return IPV4_REGEX.test(value);
    case 'AAAA':
      return IPV6_REGEX.test(value);
    case 'CNAME':
    case 'MX':
      return HOSTNAME_REGEX.test(value);
    case 'SRV':
      return SRV_REGEX.test(value);
    case 'CAA':
      return CAA_REGEX.test(value);
    case 'TXT':
      // Un guillemet interne produirait un target OVH malformé (composeOvhTarget
      // encadre la valeur de `"…"` sans échappement) — rejeté avec une erreur
      // lisible plutôt qu'un 400 brut du registrar. Longueur déjà bornée ci-dessus.
      return !value.includes('"');
    default:
      return false;
  }
}

/**
 * Statuts d'activation pour lesquels la zone OVH existe et nous appartient.
 * Exclut les états transitoires (commande, transfert, paiement) où toucher la
 * zone serait au mieux sans effet, au pire destructif. Le sous-domaine gratuit
 * `ROOT_DOMAIN` n'a pas de row `pro_website_domain` → naturellement hors scope.
 */
export const DNS_ZONE_EDITABLE_STATUSES: readonly WebsiteDomainActivationStatus[] = [
  WebsiteDomainActivationStatus.DNS_CONFIGURED,
  ...ACTIVE_WEBSITE_DOMAIN_STATUSES,
];

export const DnsZoneRecordSchema = Type.Object({
  id: Type.Number(),
  /** Type brut OVH — peut être un type hors allowlist ('NS', pseudo-type 'SPF' legacy…). */
  type: Type.String(),
  /** '' = apex. */
  subdomain: Type.String(),
  /** Valeur AFFICHABLE : TXT dé-quoté, MX/SRV sans le préfixe priorité. */
  target: Type.String(),
  /** Priorité extraite du target OVH pour MX/SRV. */
  priority: Type.Optional(Type.Number()),
  ttl: Type.Number(),
  editable: Type.Boolean(),
  managed: Type.Boolean(),
  managed_reason: Type.Optional(Type.Enum(DnsManagedReason)),
});
export type DnsZoneRecordObject = Static<typeof DnsZoneRecordSchema>;

export const DnsZoneResponseSchema = Type.Object({
  domain: Type.String(),
  records: Type.Array(DnsZoneRecordSchema),
  /** Pilote l'UI des slots mail : verrouillés quand l'hébergement mail Stalwart est actif. */
  mail_hosting_active: Type.Boolean(),
  record_count: Type.Number(),
  max_records: Type.Number(),
});
export type DnsZoneResponseObject = Static<typeof DnsZoneResponseSchema>;

export const CreateDnsRecordRequestSchema = Type.Object({
  type: Type.Union(DNS_EDITABLE_RECORD_TYPES.map((t) => Type.Literal(t))),
  subdomain: Type.String({ maxLength: 253, pattern: DNS_SUBDOMAIN_PATTERN }),
  /** Valeur nue : sans quotes TXT, sans priorité MX/SRV. */
  target: Type.String({ minLength: 1, maxLength: MAX_DNS_TARGET_LENGTH }),
  ttl: Type.Optional(Type.Number({ minimum: DNS_MIN_TTL, maximum: DNS_MAX_TTL })),
  /** MX/SRV uniquement. */
  priority: Type.Optional(Type.Number({ minimum: 0, maximum: 65535 })),
});
export type CreateDnsRecordRequestObject = Static<typeof CreateDnsRecordRequestSchema>;

// Pas de `type` : le PUT OVH ne change pas le fieldType d'un enregistrement.
export const UpdateDnsRecordRequestSchema = Type.Object({
  subdomain: Type.String({ maxLength: 253, pattern: DNS_SUBDOMAIN_PATTERN }),
  target: Type.String({ minLength: 1, maxLength: MAX_DNS_TARGET_LENGTH }),
  ttl: Type.Optional(Type.Number({ minimum: DNS_MIN_TTL, maximum: DNS_MAX_TTL })),
  priority: Type.Optional(Type.Number({ minimum: 0, maximum: 65535 })),
});
export type UpdateDnsRecordRequestObject = Static<typeof UpdateDnsRecordRequestSchema>;

export const DnsRecordMutationResponseSchema = Type.Object({
  success: Type.Boolean(),
  /** Renseigné sur create/update. */
  record: Type.Optional(DnsZoneRecordSchema),
});
export type DnsRecordMutationResponseObject = Static<typeof DnsRecordMutationResponseSchema>;

// =============================================================================
// Diagnostic messagerie (lecture seule) — « Vérifier ma messagerie »
// =============================================================================
// Compare ce que la zone OVH déclare avec ce que le DNS public répond, et le
// résume en quatre lignes lisibles par un non-technicien : réception des emails
// (MX), autorisation d'envoi (SPF), politique anti-usurpation (DMARC), signatures
// (DKIM). Aucune écriture. Le pro n'a pas à savoir ce qu'est un MX : le front
// traduit `status` + `kind` en phrases.
// =============================================================================

export enum MailDnsCheckStatus {
  OK = 'OK',
  /** Fonctionne, mais quelque chose mérite attention (SPF neutre, DMARC absent…). */
  WARNING = 'WARNING',
  /** Attendu et absent (pas de MX, pas de SPF…). */
  MISSING = 'MISSING',
  /** Incohérence bloquante (deux SPF, MX déclaré ≠ MX public…). */
  ERROR = 'ERROR',
}

/** Qui reçoit les emails du domaine, d'après les MX publiés. */
export enum MailDnsProviderKind {
  /** MX vers notre serveur mail (boîtes hébergées par la plateforme). */
  PLATFORM = 'PLATFORM',
  /** MX vers une messagerie tierce (Office 365, Google Workspace, OVH…). */
  EXTERNAL = 'EXTERNAL',
  /** Aucun MX : le domaine ne reçoit pas d'emails. */
  NONE = 'NONE',
  /** MX mélangés (plateforme + tiers) : la réception est aléatoire. */
  MIXED = 'MIXED',
}

export const MailDnsCheckItemSchema = Type.Object({
  status: Type.Enum(MailDnsCheckStatus),
  /** Clé de détail traduisible côté front (`MAIL_CHECK_DETAIL_*`), optionnelle. */
  detail: Type.Optional(Type.String()),
  /** Valeurs observées, pour l'affichage avancé (hôtes MX, SPF résolu…). */
  values: Type.Optional(Type.Array(Type.String())),
});
export type MailDnsCheckItemObject = Static<typeof MailDnsCheckItemSchema>;

export const MailDnsCheckResponseSchema = Type.Object({
  domain: Type.String(),
  provider: Type.Enum(MailDnsProviderKind),
  /** Résumé : le pire des quatre statuts. */
  overall: Type.Enum(MailDnsCheckStatus),
  mx: MailDnsCheckItemSchema,
  spf: MailDnsCheckItemSchema,
  dmarc: MailDnsCheckItemSchema,
  dkim: MailDnsCheckItemSchema,
  /** Vrai si l'hébergement mail de la plateforme est actif sur ce domaine (contexte des messages). */
  mail_hosting_active: Type.Boolean(),
  /** Vrai si un expéditeur de campagnes (Mailjet) est configuré sur ce domaine. */
  campaign_sender_active: Type.Boolean(),
});
export type MailDnsCheckResponseObject = Static<typeof MailDnsCheckResponseSchema>;

/** Clés de détail renvoyées par le diagnostic (traduites côté front, préfixe `MAIL_CHECK_DETAIL_`). */
export const MAIL_DNS_CHECK_DETAILS = {
  MX_NOT_PROPAGATED: 'MX_NOT_PROPAGATED',
  MX_MIXED: 'MX_MIXED',
  MX_NONE: 'MX_NONE',
  MX_EXTERNAL: 'MX_EXTERNAL',
  MX_PLATFORM: 'MX_PLATFORM',
  SPF_MISSING: 'SPF_MISSING',
  SPF_MULTIPLE: 'SPF_MULTIPLE',
  SPF_NOT_PROPAGATED: 'SPF_NOT_PROPAGATED',
  SPF_PLATFORM_INCLUDE_MISSING: 'SPF_PLATFORM_INCLUDE_MISSING',
  SPF_CAMPAIGN_INCLUDE_MISSING: 'SPF_CAMPAIGN_INCLUDE_MISSING',
  SPF_OK: 'SPF_OK',
  DMARC_MISSING: 'DMARC_MISSING',
  DMARC_NOT_PROPAGATED: 'DMARC_NOT_PROPAGATED',
  DMARC_INVALID: 'DMARC_INVALID',
  DMARC_OK: 'DMARC_OK',
  DKIM_NONE: 'DKIM_NONE',
  DKIM_NOT_PROPAGATED: 'DKIM_NOT_PROPAGATED',
  DKIM_OK: 'DKIM_OK',
  /** Hébergement mail en cours de mise en place : le job DNS n'a pas encore écrit la zone. */
  MAIL_PROVISIONING: 'MAIL_PROVISIONING',
} as const;
export type MailDnsCheckDetail = (typeof MAIL_DNS_CHECK_DETAILS)[keyof typeof MAIL_DNS_CHECK_DETAILS];
